import { fromNumericKind, snapToStep, type NumericKind } from '@anytracker/core'
import { useId, useState, type ChangeEvent } from 'react'

import styles from './NumericInput.module.css'
import type { ValueInputProps } from './types.js'

interface Props extends ValueInputProps {
  readonly kind: NumericKind
}

/**
 * A slider with a large readout, plus a number field for precision.
 *
 * The snapping rule is `snapToStep` from core, not arithmetic here. A slider
 * that produced 0.30000000000000004, or a value between two steps, would store
 * it forever — and the same rule has to apply identically on native, so it is
 * domain logic rather than a display concern.
 */
export function NumericInput({
  track,
  kind,
  value,
  onChange,
  disabled = false,
  labelledBy,
  describedBy,
}: Props) {
  const scale = fromNumericKind(kind)
  const numeric = typeof value === 'number' ? value : null
  const preciseId = useId()

  /**
   * What the number field shows while being typed in.
   *
   * Held separately because snapping every keystroke makes the field
   * unusable — typing "7" toward "7.5" would jump to 7 and eat the decimal
   * point. Snap on blur instead. `null` means "show the committed value".
   */
  const [typing, setTyping] = useState<string | null>(null)

  // The slider needs a position even with nothing recorded. It starts at the
  // midpoint, which reads as "unset" far better than the minimum does — a
  // hunger slider parked at 1 looks like an answer.
  const sliderValue = numeric ?? snapToStep((scale.min + scale.max) / 2, scale)

  function handleSlider(event: ChangeEvent<HTMLInputElement>) {
    onChange(snapToStep(Number(event.target.value), scale))
  }

  function commitTyped() {
    if (typing === null) {
      return
    }
    const trimmed = typing.trim()
    setTyping(null)
    if (trimmed === '') {
      onChange(null)
      return
    }
    const parsed = Number(trimmed)
    if (Number.isFinite(parsed)) {
      onChange(snapToStep(parsed, scale))
    }
  }

  const displayed = numeric === null ? '—' : numeric.toFixed(scale.decimals)

  return (
    <div className={styles['root']}>
      <div className={styles['readout']}>
        <span
          className={`${styles['value']} ${numeric === null ? styles['unmeasured'] : ''}`}
          // The slider already announces its value; a second live reading of
          // the same number would double up on every drag.
          aria-hidden="true"
        >
          {displayed}
        </span>
        {track.unit !== undefined && <span className={styles['unit']}>{track.unit}</span>}
      </div>

      <input
        type="range"
        className={styles['slider']}
        min={scale.min}
        max={scale.max}
        step={scale.step}
        value={sliderValue}
        disabled={disabled}
        onChange={handleSlider}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        // Without this a screen reader reads the raw number. With it, an
        // unmeasured track says so rather than announcing the midpoint as
        // though it were a recorded value.
        aria-valuetext={
          numeric === null
            ? 'Not measured'
            : `${displayed}${track.unit === undefined ? '' : ` ${track.unit}`}`
        }
      />

      <div className={styles['bounds']} aria-hidden="true">
        <span>{scale.min}</span>
        <span>{scale.max}</span>
      </div>

      <div className={styles['precise']}>
        <label className={styles['preciseLabel']} htmlFor={preciseId}>
          Exact
        </label>
        <input
          id={preciseId}
          type="number"
          inputMode="decimal"
          className={styles['preciseField']}
          min={scale.min}
          max={scale.max}
          step={scale.step}
          disabled={disabled}
          value={typing ?? (numeric === null ? '' : String(numeric))}
          onChange={(event) => setTyping(event.target.value)}
          onBlur={commitTyped}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              commitTyped()
            }
          }}
          aria-describedby={describedBy}
        />
      </div>
    </div>
  )
}
